'use client'

import React, { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import posthog from 'posthog-js'
import type { StatsCalculationMethod, VisaCategoryStat } from './visaCategoryStats'
import { VisaCategoryResults } from './VisaCategoryResults'

interface StatsClientProps {
  categories: VisaCategoryStat[]
  calculationMethod: StatsCalculationMethod
}

export function StatsClient({ categories, calculationMethod }: StatsClientProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [selectedId, setSelectedId] = useState(categories[0]?.id ?? '')

  const selectedCategory =
    categories.find((category) => category.id === selectedId) ?? categories[0]

  if (!selectedCategory) return null

  const handleCategorySelect = (id: string, label: string) => {
    setSelectedId(id)
    posthog.capture('visa_category_stat_selected', {
      visa_category: label,
      page_url: window.location.pathname,
    })
  }

  return (
    <section className="py-10 md:py-14 px-6 md:px-12 bg-primary">
      <div className="max-w-380 mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <h2 className="text-center text-xl md:text-3xl font-medium text-white mb-7 md:mb-8">
            How do we perform on{' '}
            <span className="italic font-serif text-brand-gold-light">your</span> case type?
          </h2>

          <div
            className="-mx-6 md:mx-0 overflow-x-auto scrollbar-none"
            role="tablist"
            aria-label="Case types"
          >
            <div className="flex w-max md:w-full md:flex-wrap md:justify-center gap-0 px-6 md:px-0 border-b border-white/10">
              {categories.map((category) => {
                const isSelected = category.id === selectedId

                return (
                  <button
                    key={category.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => handleCategorySelect(category.id, category.label)}
                    onMouseEnter={() => setSelectedId(category.id)}
                    className={`relative shrink-0 px-4 md:px-5 py-2.5 md:py-3 text-base md:text-lg font-medium transition-colors ${isSelected ? 'text-white' : 'text-white/45 hover:text-white/70'
                      }`}
                  >
                    {category.label}
                    {isSelected && (
                      <motion.span
                        layoutId="stats-tab-indicator"
                        className="absolute inset-x-1 md:inset-x-2 -bottom-px h-0.5 bg-brand-gold-light rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <VisaCategoryResults
            category={selectedCategory}
            calculationMethod={calculationMethod}
          />

          <p className="mt-8 md:mt-10 border-t border-white/10 pt-5 text-xs leading-relaxed text-white/45">
            <span className="font-semibold text-white/70">Past results do not guarantee a similar outcome.</span>{' '}
            Every immigration matter is unique and depends on its own facts and applicable law.
            The approval rates shown reflect historical outcomes for cases we have handled and are
            not a prediction, promise, or guarantee of the result in any particular case. This
            information is provided for general informational purposes only and does not constitute
            legal advice or the formation of an attorney-client relationship.
          </p>
        </motion.div>
      </div>
    </section>
  )
}
